import type { ProductAd } from '@/scopes/product-ads/domain/types'
import { useOnScreen } from '@/scopes/common/components/hooks/useOnScreen'
import { MediaServerLazyImage } from '@/scopes/common/components/MediaServerLazyImage'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { createProductAdItemClickedEvent } from '@/scopes/product-ads/domain/analytics'
import { useProductAdsRendered, useUpdateProductAdViewed } from '@/scopes/product-ads/domain/hooks'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import { useEffect, useRef, useState } from 'react'
import styles from './ProductAd.module.css'

interface ProductAdProps {
  data: ProductAd
  componentType: string
}

export function ProductAdComponent({ data, componentType }: ProductAdProps) {
  const ref = useRef<HTMLDivElement>(null)
  const isOnScreen = useOnScreen(ref, { threshold: 0.5 })
  const [isViewed, setIsViewed] = useState(false)
  const { sendEvent } = useAnalytics()

  // we notify the productAd is rendered
  useProductAdsRendered(data.rendered)

  const { update } = useUpdateProductAdViewed(data.viewed)

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> = setTimeout(() => {}, 0)
    if (isOnScreen && !isViewed) {
      timer = setTimeout(async () => {
        if (isOnScreen) {
          await update()
          setIsViewed(true)
        }
      }, 1000)
    }
    else {
      clearTimeout(timer)
    }

    return () => clearTimeout(timer)
  }, [data.viewed, isOnScreen, isViewed, update])

  const handleOnClick = () => {
    localStorage.setItem('C_URL', data.conversionUrl ?? '')
    localStorage.setItem('acco_ID', data.accoId)
    sendEvent(createProductAdItemClickedEvent({ ads: [data], type: componentType }))
  }

  return (
    <div ref={ref} className={`${styles.wrapper} FID_${data.flightId}`}>
      <a className={styles.link} href={data.clicked} target="_blank" rel="noreferrer" onClick={handleOnClick}>
        <div className={styles.imageWrapper}>
          <MediaServerLazyImage
            src={data.image}
            widths={[260, 147]}
            alt={data.heading}
          />
        </div>
        <div className={styles.footer}>
          <Typography variant="display-i-regular">{data.heading}</Typography>
          <Typography className={styles.subHeading} variant="body-s-regular-tight">
            {data.subHeading}
          </Typography>
        </div>
      </a>
    </div>
  )
}
